import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash2, Edit2, Save, X, Users, User, Shield, Eye, AlertTriangle, Settings } from 'lucide-react';
import { apiService } from '../../services/api';
import { ApiTeam, ApiUser, CreateTeamRequest } from '../../services/api';
import { useRealTime } from '../../contexts/RealTimeContext';

interface TeamManagementProps {
  isLoading?: boolean;
  onRefresh?: () => void;
}

interface SupervisorTeamAssignment {
  supervisorId: number;
  supervisorName: string;
  teamIds: number[];
}

const TeamManagement: React.FC<TeamManagementProps> = ({ isLoading, onRefresh }) => {
  const [teams, setTeams] = useState<ApiTeam[]>([]);
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [newTeamName, setNewTeamName] = useState('');
  const [selectedSupervisor, setSelectedSupervisor] = useState<string>('');
  const [selectedMonitor, setSelectedMonitor] = useState<string>('');
  const [editingTeam, setEditingTeam] = useState<number | null>(null);
  const [editTeamName, setEditTeamName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<{
    id: number;
    name: string;
    membersCount: number;
    supervisorName: string;
    monitorName: string;
  } | null>(null);
  const [supervisorDialogOpen, setSupervisorDialogOpen] = useState(false);
  const [selectedSupervisorForTeams, setSelectedSupervisorForTeams] = useState<number | null>(null);
  const [supervisorTeamAssignments, setSupervisorTeamAssignments] = useState<Map<number, number[]>>(new Map());
  const [selectedWorkerPerTeam, setSelectedWorkerPerTeam] = useState<Map<number, string>>(new Map());
  
  // Real-time updates
  const { subscribeToTeamEvents } = useRealTime();

  useEffect(() => {
    loadData();
  }, []);
  
  // Subscribe to real-time team composition changes
  useEffect(() => {
    if (!subscribeToTeamEvents) {
      console.warn('subscribeToTeamEvents not available');
      return;
    }
    
    let debounceTimer: NodeJS.Timeout;
    
    subscribeToTeamEvents((event) => {
      console.log('Team event received:', event);
      
      // Clear existing timer
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      
      // Debounce reload to avoid multiple rapid updates
      debounceTimer = setTimeout(() => {
        loadData();
      }, 500);
    });

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [subscribeToTeamEvents]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [teamsData, usersData] = await Promise.all([
        apiService.getTeams(),
        apiService.getAllUsers()
      ]);
      setTeams(teamsData);
      setUsers(usersData);
      
      // Clear selected workers when reloading
      setSelectedWorkerPerTeam(new Map());
      
      // Build supervisor team assignments
      const assignments = new Map<number, number[]>();
      usersData.filter(u => u.role === 'supervisor').forEach(supervisor => {
        const supervisorTeams = teamsData
          .filter(team => team.supervisorId === supervisor.id)
          .map(team => team.id);
        if (supervisorTeams.length > 0) {
          assignments.set(supervisor.id, supervisorTeams);
        }
      });
      setSupervisorTeamAssignments(assignments);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const supervisors = users.filter(u => u.role === 'supervisor');
  const monitors = users.filter(u => u.role === 'monitor');
  const workers = users.filter(u => u.role === 'worker');

  const handleCreateTeam = async () => {
    if (!newTeamName.trim() || !selectedSupervisor || !selectedMonitor) {
      setError('Completați toate câmpurile');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data: CreateTeamRequest = {
        name: newTeamName.trim(),
        supervisorId: parseInt(selectedSupervisor),
        monitorId: parseInt(selectedMonitor)
      };
      
      await apiService.createTeam(data);
      setNewTeamName('');
      setSelectedSupervisor('');
      setSelectedMonitor('');
      // Call parent refresh if provided
      await onRefresh?.();
      // Real-time updates will handle the reload
    } catch (error) {
      console.error('Error creating team:', error);
      setError('Eroare la crearea echipei');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (id: number, name: string, membersCount: number, supervisorName: string, monitorName: string) => {
    setTeamToDelete({
      id,
      name,
      membersCount,
      supervisorName,
      monitorName
    });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!teamToDelete) return;

    setLoading(true);
    setError(null);
    setDeleteDialogOpen(false);
    
    try {
      // Use regular delete since soft delete is temporarily disabled
      await apiService.deleteTeam(teamToDelete.id);
      
      // Success message
      alert(`✅ Echipa "${teamToDelete.name}" a fost ștearsă cu succes!`);
      
      // Call parent refresh if provided
      await onRefresh?.();
      // Real-time updates will handle the reload
    } catch (error) {
      console.error('Error deleting team:', error);
      setError('Eroare la ștergerea echipei. Vă rugăm să încercați din nou.');
    } finally {
      setLoading(false);
      setTeamToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setTeamToDelete(null);
  };

  const handleUpdateTeam = async (id: number) => {
    if (!editTeamName.trim()) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await apiService.updateTeam(id, { name: editTeamName.trim() });
      setEditingTeam(null);
      setEditTeamName('');
      // Call parent refresh first
      await onRefresh?.();
      // Then reload local data
      await loadData();
    } catch (error) {
      console.error('Error updating team:', error);
      setError('Eroare la actualizarea echipei');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUserToTeam = async (teamId: number, userId: number) => {
    setLoading(true);
    setError(null);
    try {
      await apiService.addUserToTeam(teamId, userId);
      // Clear the selection for this team
      const newSelections = new Map(selectedWorkerPerTeam);
      newSelections.delete(teamId);
      setSelectedWorkerPerTeam(newSelections);
      // Call parent refresh if provided
      await onRefresh?.();
      // Real-time updates will handle the reload automatically
    } catch (error) {
      console.error('Error adding user to team:', error);
      setError('Eroare la adăugarea utilizatorului în echipă');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUserFromTeam = async (teamId: number, userId: number, userName: string) => {
    if (!confirm(`Sigur vrei să îndepărtezi pe ${userName} din echipă?`)) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await apiService.removeUserFromTeam(teamId, userId);
      // Call parent refresh if provided
      await onRefresh?.();
      // Real-time updates will handle the reload automatically
    } catch (error) {
      console.error('Error removing user from team:', error);
      setError('Eroare la îndepărtarea utilizatorului din echipă');
    } finally {
      setLoading(false);
    }
  };

  const getUnassignedWorkers = () => {
    return workers.filter(w => !w.teamId);
  };

  const getSupervisorName = (supervisorId: number) => {
    const supervisor = users.find(u => u.id === supervisorId);
    return supervisor?.name || 'Necunoscut';
  };

  const getMonitorName = (monitorId: number) => {
    const monitor = users.find(u => u.id === monitorId);
    return monitor?.name || 'Necunoscut';
  };

  const handleOpenSupervisorDialog = (supervisorId: number) => {
    setSelectedSupervisorForTeams(supervisorId);
    setSupervisorDialogOpen(true);
  };

  const handleToggleTeamForSupervisor = async (teamId: number) => {
    if (!selectedSupervisorForTeams) return;
    
    const currentTeams = supervisorTeamAssignments.get(selectedSupervisorForTeams) || [];
    const team = teams.find(t => t.id === teamId);
    if (!team) return;
    
    try {
      setLoading(true);
      
      if (currentTeams.includes(teamId)) {
        // Remove team from supervisor
        await apiService.updateTeam(teamId, { supervisorId: null });
      } else {
        // Add team to supervisor
        await apiService.updateTeam(teamId, { supervisorId: selectedSupervisorForTeams });
      }
      
      // Reload data
      await loadData();
    } catch (error) {
      console.error('Error updating team supervisor:', error);
      setError('Eroare la actualizarea supervizorului echipei');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong>Eroare:</strong> {error}
        </div>
      )}

      {/* Create New Team */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Creează Echipă Nouă
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="teamName">Nume echipă</Label>
            <Input
              id="teamName"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              placeholder="Introdu numele echipei"
              disabled={loading}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="supervisor">Supervisor</Label>
              <Select 
                value={selectedSupervisor} 
                onValueChange={setSelectedSupervisor}
                disabled={loading}
              >
                <SelectTrigger id="supervisor">
                  <SelectValue placeholder="Selectează supervisor" />
                </SelectTrigger>
                <SelectContent>
                  {supervisors.map(supervisor => (
                    <SelectItem key={supervisor.id} value={supervisor.id.toString()}>
                      {supervisor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="monitor">Monitor</Label>
              <Select 
                value={selectedMonitor} 
                onValueChange={setSelectedMonitor}
                disabled={loading}
              >
                <SelectTrigger id="monitor">
                  <SelectValue placeholder="Selectează monitor" />
                </SelectTrigger>
                <SelectContent>
                  {monitors.map(monitor => (
                    <SelectItem key={monitor.id} value={monitor.id.toString()}>
                      {monitor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            onClick={handleCreateTeam}
            className="w-full"
            disabled={loading || !newTeamName.trim() || !selectedSupervisor || !selectedMonitor}
          >
            <Plus className="w-4 h-4 mr-2" />
            Creează Echipă
          </Button>
        </CardContent>
      </Card>

      {/* Supervisor Teams Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Gestiune Echipe Supervizori
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-600 mb-4">
            Asignează multiple echipe la supervizori. Un supervizor poate gestiona mai multe echipe simultan.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {supervisors.map(supervisor => {
              const teamCount = supervisorTeamAssignments.get(supervisor.id)?.length || 0;
              return (
                <Card key={supervisor.id} className="border hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-medium">{supervisor.name}</h4>
                        <p className="text-sm text-gray-500">
                          {teamCount} {teamCount === 1 ? 'echipă' : 'echipe'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenSupervisorDialog(supervisor.id)}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                    </div>
                    {teamCount > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {teams
                          .filter(team => supervisorTeamAssignments.get(supervisor.id)?.includes(team.id))
                          .map(team => (
                            <Badge key={team.id} variant="secondary" className="text-xs">
                              {team.name}
                            </Badge>
                          ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Teams List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Echipe Existente ({teams.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {teams.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Nu există echipe create.</p>
          ) : (
            teams.map(team => (
              <Card key={team.id} className="border-l-4 border-l-blue-500">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    {editingTeam === team.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          value={editTeamName}
                          onChange={(e) => setEditTeamName(e.target.value)}
                          className="max-w-xs"
                          onKeyPress={(e) => e.key === 'Enter' && handleUpdateTeam(team.id)}
                        />
                        <Button
                          size="sm"
                          onClick={() => handleUpdateTeam(team.id)}
                          disabled={loading}
                        >
                          <Save className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingTeam(null);
                            setEditTeamName('');
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <CardTitle className="text-lg">{team.name}</CardTitle>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingTeam(team.id);
                              setEditTeamName(team.name);
                            }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => openDeleteDialog(
                              team.id, 
                              team.name, 
                              team.members.length,
                              getSupervisorName(team.supervisorId),
                              getMonitorName(team.monitorId)
                            )}
                            disabled={loading}
                            title="Șterge echipa (soft delete - păstrează logurile)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-gray-600">Supervisor:</span>
                      <span className="font-medium">{getSupervisorName(team.supervisorId)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-gray-600">Monitor:</span>
                      <span className="font-medium">{getMonitorName(team.monitorId)}</span>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Membri ({team.members.length})
                    </h4>
                    <div className="space-y-2">
                      {team.members.length === 0 ? (
                        <p className="text-gray-500 text-sm">Nu sunt membri în această echipă.</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {team.members.map(member => (
                            <div key={member.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <span className="text-sm">{member.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {member.role === 'worker' ? 'Muncitor' : member.role}
                              </Badge>
                              {member.role === 'worker' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleRemoveUserFromTeam(team.id, member.id, member.name)}
                                  disabled={loading}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Add Workers to Team */}
                  <div>
                    <Label className="text-sm">Adaugă muncitori în echipă</Label>
                    <Select 
                      key={`select-${team.id}-${team.members.length}-${getUnassignedWorkers().length}`} // Force re-render when team members or available workers change
                      value={selectedWorkerPerTeam.get(team.id) || ""} // Use controlled value
                      onValueChange={(value) => {
                        // Update selection state
                        const newSelections = new Map(selectedWorkerPerTeam);
                        newSelections.set(team.id, value);
                        setSelectedWorkerPerTeam(newSelections);
                        // Add user to team
                        handleAddUserToTeam(team.id, parseInt(value));
                      }}
                      disabled={loading || getUnassignedWorkers().length === 0}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder={getUnassignedWorkers().length === 0 ? "Nu sunt muncitori disponibili" : "Selectează un muncitor"} />
                      </SelectTrigger>
                      <SelectContent>
                        {getUnassignedWorkers().length === 0 ? (
                          <SelectItem value="none" disabled>Nu sunt muncitori disponibili</SelectItem>
                        ) : (
                          getUnassignedWorkers().map(worker => (
                            <SelectItem key={worker.id} value={worker.id.toString()}>
                              {worker.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirmare Ștergere Echipă
            </DialogTitle>
          </DialogHeader>
          
          {teamToDelete && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Echipa:</span>
                    <span className="font-bold text-red-800">{teamToDelete.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Supervisor:</span>
                    <span className="text-gray-900">{teamToDelete.supervisorName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Monitor:</span>
                    <span className="text-gray-900">{teamToDelete.monitorName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Membri:</span>
                    <span className="font-bold text-blue-800">{teamToDelete.membersCount} muncitori</span>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">⚠️ Această acțiune va:</h4>
                <ul className="space-y-1 text-sm text-yellow-700">
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    Șterge echipa din sistem (soft delete)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    Păstra toate înregistrările și logurile existente
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    Dezactiva echipa fără a afecta istoricul
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    Elibera supervisorul și monitorul pentru alte echipe
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    Elibera muncitorii pentru redistribuire în alte echipe
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <strong>Notă:</strong> Muncitorii din această echipă vor deveni neasignați și vor putea fi adăugați în alte echipe. 
                  Toate datele istorice rămân intacte.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleCancelDelete}
              disabled={loading}
            >
              Anulează
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? (
                <>Se șterge...</>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Confirmă Ștergerea
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supervisor Team Assignment Dialog */}
      <Dialog open={supervisorDialogOpen} onOpenChange={setSupervisorDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Asignare Echipe pentru {selectedSupervisorForTeams ? supervisors.find(s => s.id === selectedSupervisorForTeams)?.name : ''}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[400px] overflow-y-auto">
            <p className="text-sm text-gray-600">
              Selectează echipele pe care le va gestiona acest supervizor. Poate avea mai multe echipe asignate.
            </p>
            
            {teams.map(team => {
              const isAssigned = supervisorTeamAssignments.get(selectedSupervisorForTeams || 0)?.includes(team.id) || false;
              const hasOtherSupervisor = team.supervisorId && team.supervisorId !== selectedSupervisorForTeams;
              
              return (
                <Card key={team.id} className={`border ${isAssigned ? 'border-blue-500 bg-blue-50' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium">{team.name}</h4>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {team.members.length} membri
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {getMonitorName(team.monitorId)}
                          </span>
                          {hasOtherSupervisor && (
                            <span className="flex items-center gap-1 text-amber-600">
                              <Shield className="w-3 h-3" />
                              {getSupervisorName(team.supervisorId)}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={isAssigned ? "default" : "outline"}
                        onClick={() => handleToggleTeamForSupervisor(team.id)}
                        disabled={loading}
                      >
                        {isAssigned ? 'Asignat' : 'Asignează'}
                      </Button>
                    </div>
                    {hasOtherSupervisor && (
                      <p className="text-xs text-amber-600 mt-2">
                        ⚠️ Această echipă are deja un supervizor asignat
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSupervisorDialogOpen(false)}
            >
              Închide
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagement;